import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["persona", "channel", "quantity", "name", "version", "estimateCount", "traits", "status", "generate", "quantityHint", "usageNote"]
  static values = { previewUrl: String, allowance: Number, autoName: Boolean }

  connect() {
    this.revision = 0
    this.previewPending = false
    this.nameEdited = !this.autoNameValue
    this.updateTraits()
    this.queuePreview(0)
  }

  disconnect() {
    window.clearTimeout(this.previewTimer)
  }

  changed(event) {
    if (event?.currentTarget === this.nameTarget) this.nameEdited = true
    else if (!this.nameEdited) this.updateDefaultName()
    this.revision += 1
    this.availability = null
    this.estimateCountTarget.textContent = "—"
    this.updateTraits()
    this.queuePreview(500)
  }

  quantityChanged() {
    this.updateGeneration()
  }

  submit(event) {
    if (this.generateTarget.disabled || this.previewPending) event.preventDefault()
  }

  queuePreview(delay) {
    window.clearTimeout(this.previewTimer)
    this.generateTarget.disabled = true

    const reason = this.inputError()
    if (reason) {
      this.statusTarget.textContent = reason
      this.quantityHintTarget.textContent = reason
      return
    }

    this.statusTarget.textContent = "Checking potential matches…"
    this.quantityHintTarget.textContent = "Checking the matching audience before generating."
    this.previewTimer = window.setTimeout(() => this.preview(), delay)
  }

  async preview() {
    if (this.previewPending) return

    this.previewPending = true
    const revision = this.revision
    const persona = this.selectedPersona

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({
          job_id: this.element.querySelector('[name="job_id"]').value,
          audience_name: this.nameTarget.value,
          selected_channels: this.selectedChannels,
          selected_persona_key: persona.value,
          preview_version: this.versionTarget.value
        })
      })
      const result = await response.json()
      if (revision !== this.revision || !this.element.isConnected) return

      if (!response.ok) {
        if (response.status === 409 && result.preview_version) {
          this.versionTarget.value = result.preview_version
          this.revision += 1
          return
        }
        this.availability = null
        this.statusTarget.textContent = result.error || "We couldn’t check this audience. Change a selection to try again."
        this.quantityHintTarget.textContent = "An estimate is required before generating."
        return
      }

      this.availability = result
      this.versionTarget.value = result.preview_version
      this.estimateCountTarget.textContent = Number(result.potential_matches).toLocaleString()
      this.statusTarget.textContent = this.availabilityMessage(result)
      this.updateGeneration()
    } catch {
      if (revision === this.revision && this.element.isConnected) {
        this.availability = null
        this.statusTarget.textContent = "We couldn’t check this audience. Change a selection to try again."
        this.quantityHintTarget.textContent = "An estimate is required before generating."
      }
    } finally {
      this.previewPending = false
      if (revision !== this.revision && this.element.isConnected) this.queuePreview(0)
      else this.updateGeneration()
    }
  }

  updateGeneration() {
    const quantity = Number(this.quantityTarget.value)
    const available = this.availability?.max_generatable_now || 0
    const valid = !this.previewPending && this.availability?.eligible && Number.isInteger(quantity) && quantity > 0 && quantity <= available
    this.generateTarget.disabled = !valid
    this.generateTarget.textContent = quantity > 0 ? `Generate ${quantity.toLocaleString()} leads` : "Generate leads"

    if (!this.availability) return

    const floor = this.availability.minimum_required
    if (quantity > available) this.quantityHintTarget.textContent = `Choose no more than ${available.toLocaleString()} leads available now.`
    else if (quantity < 1 || !Number.isInteger(quantity)) this.quantityHintTarget.textContent = "Enter a whole number of leads."
    else if (floor > quantity) this.quantityHintTarget.textContent = `${floor.toLocaleString()}+ is recommended for your selected channels. You can still generate a smaller list.`
    else this.quantityHintTarget.textContent = `${floor.toLocaleString()}+ works for your selected channels. Up to ${available.toLocaleString()} can be generated now.`

    this.usageNoteTarget.textContent = valid ? `Uses ${quantity.toLocaleString()} of your ${this.allowanceValue.toLocaleString()} leads left. Failed runs don’t count.` : "Failed runs do not use your monthly allowance."
  }

  updateTraits() {
    const chips = JSON.parse(this.selectedPersona?.dataset.chips || "[]")
    this.traitsTarget.replaceChildren(...chips.map(chip => {
      const item = document.createElement("span")
      item.textContent = chip
      return item
    }))
  }

  updateDefaultName() {
    const name = this.selectedPersona?.dataset.name
    if (!name) return

    const channel = this.channelTargets.find(input => input.checked)?.dataset.label
    this.nameTarget.value = [ `${name} lookalikes`, channel ].filter(Boolean).join(', ').slice(0, 80)
  }

  inputError() {
    if (!this.selectedPersona) return "Choose a persona from this series."
    if (Object.keys(JSON.parse(this.selectedPersona.dataset.filters || "{}")).length === 0) return "This persona has no demographic details available for a lead estimate."
    if (this.selectedChannels.length === 0) return "Choose at least one marketing channel."
    if (this.nameTarget.value.trim().length < 3) return "Enter a list name of at least 3 characters."
    if (this.allowanceValue <= 0) return "No leads remain in this month’s allowance."
    return null
  }

  availabilityMessage(result) {
    if (result.reason === "no_matches") return "No matching leads found. Choose another persona."
    if (result.reason === "allowance_exhausted") return "No leads remain in this month’s allowance."
    if (result.reason === "plan_unavailable") return "Your plan does not currently include lead generation."
    if (result.reason === "insufficient_matches") return "Fewer matches than the recommended channel minimum. A smaller list may still be useful."
    if (result.reason === "allowance_below_recommendation") return "Your remaining allowance is below the recommended channel minimum."
    return "Estimate ready. The final lead count may differ when the list is generated."
  }

  get selectedPersona() {
    return this.personaTargets.find(input => input.checked)
  }

  get selectedChannels() {
    return this.channelTargets.filter(input => input.checked).map(input => input.value)
  }
}
